/**
 * Linear regression (least-squares) for trend line overlay.
 * Input: array of y-values (one per bar, left to right).
 * Output: slope and intercept for the line y = slope * x + intercept.
 */
export function linearRegression(values: number[]): { slope: number; intercept: number } {
  const n = values.length;
  if (n < 2) return { slope: 0, intercept: values[0] || 0 };

  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
  for (let i = 0; i < n; i++) {
    sumX += i;
    sumY += values[i];
    sumXY += i * values[i];
    sumX2 += i * i;
  }
  const denom = n * sumX2 - sumX * sumX;
  if (denom === 0) return { slope: 0, intercept: sumY / n };

  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  return { slope, intercept };
}

/**
 * Aggregate data into a distribution suitable for pie/donut charts.
 * Input: array of items with a category key.
 * Output: sorted array of { label, value, pct } objects.
 */
export function buildDistribution(
  items: Array<{ category: string; value: number }>,
): Array<{ label: string; value: number; pct: number }> {
  const map = new Map<string, number>();
  for (const item of items) {
    map.set(item.category, (map.get(item.category) || 0) + item.value);
  }
  const total = [...map.values()].reduce((s, v) => s + v, 0) || 1;
  return [...map.entries()]
    .map(([label, value]) => ({ label, value, pct: Math.round((value / total) * 1000) / 10 }))
    .sort((a, b) => b.value - a.value);
}

/**
 * Compare two sets of numbers and return delta + direction.
 */
export function compareValues(
  current: number,
  previous: number,
): { delta: number; pct: number; direction: "up" | "down" | "flat" } {
  const delta = current - previous;
  const pct = previous !== 0 ? Math.round((delta / previous) * 1000) / 10 : 0;
  const direction = delta > 0 ? "up" : delta < 0 ? "down" : "flat";
  return { delta, pct, direction } as const;
}
